import React, { useState, useEffect } from 'react';
import { mbtiApi, peopleApi } from '../api';
import { useAppBackHandler } from '../navigation/AppNavigation';

interface MBTIQuestion {
  id: number;
  question: string;
  option_a: string;
  option_b: string;
  dimension: string;
}

interface MBTIResult {
  id: number;
  person_id: number | null;
  user_id?: number | null;
  mbti_type: string;
  ei_score: number;
  sn_score: number;
  tf_score: number;
  jp_score: number;
  description: string;
  strengths: string;
  weaknesses: string;
  created_at: string;
  person_name: string | null;
}

interface Person {
  id: number;
  name: string;
}

interface MBTICompatibility {
  type1: string;
  type2: string;
  compatibility_score: number;
  relationship_type: string;
  description: string;
}

// MBTI类型的emoji和名称映射
const TYPE_INFO: Record<string, { name: string; emoji: string; color: string }> = {
  ISTJ: { name: '检查员', emoji: '📋', color: '#4A6FA5' },
  ISFJ: { name: '保护者', emoji: '🛡️', color: '#6B8E6B' },
  INFJ: { name: '提倡者', emoji: '🌟', color: '#9B59B6' },
  INTJ: { name: '建筑师', emoji: '🏗️', color: '#2C3E50' },
  ISTP: { name: '鉴赏家', emoji: '🔧', color: '#7F8C8D' },
  ISFP: { name: '探险家', emoji: '🎨', color: '#E67E22' },
  INFP: { name: '调停者', emoji: '💫', color: '#8E44AD' },
  INTP: { name: '逻辑学家', emoji: '🧪', color: '#16A085' },
  ESTP: { name: '企业家', emoji: '🚀', color: '#E74C3C' },
  ESFP: { name: '表演者', emoji: '🎭', color: '#F39C12' },
  ENFP: { name: '竞选者', emoji: '🌈', color: '#E91E63' },
  ENTP: { name: '辩论家', emoji: '⚡', color: '#FF5722' },
  ESTJ: { name: '总经理', emoji: '👔', color: '#34495E' },
  ESFJ: { name: '执政官', emoji: '🤝', color: '#27AE60' },
  ENFJ: { name: '主人公', emoji: '🦸', color: '#2980B9' },
  ENTJ: { name: '指挥官', emoji: '⚔️', color: '#C0392B' },
};

// 16种人格的详细描述
const TYPE_DETAILS: Record<string, {
  group: string;
  groupColor: string;
  summary: string;
  description: string;
  strengths: string[];
  weaknesses: string[];
  careers: string[];
  famous: string[];
  loveStyle: string;
  percentage: string;
}> = {
  INTJ: {
    group: '分析家', groupColor: '#9B59B6',
    summary: '富有想象力和战略性的思想家，一切皆在计划之中。',
    description: 'INTJ是最独立的人格类型之一。他们拥有强大的分析能力和创造性思维，善于制定长期战略计划。他们追求知识和能力，对自己和他人都有很高的标准。INTJ通常是完美主义者，他们相信通过努力和智慧可以改善任何系统。',
    strengths: ['战略思维', '独立自主', '意志坚定', '知识渊博', '高效率'],
    weaknesses: ['过于自信', '不善社交', '过度分析', '对情感不敏感', '完美主义'],
    careers: ['科学家', '系统架构师', '投资分析师', '项目经理', '大学教授'],
    famous: ['尼古拉·特斯拉', '马克·扎克伯格', '埃隆·马斯克', '牛顿'],
    loveStyle: '在感情中忠诚而深沉，寻找智力上的伴侣，不善于表达情感但行动上非常可靠。',
    percentage: '2.1%',
  },
  INTP: {
    group: '分析家', groupColor: '#9B59B6',
    summary: '具有创造力的发明家，对知识有着不可抑制的渴望。',
    description: 'INTP是逻辑思维最强的类型之一。他们热爱理论和抽象概念，总是在寻找宇宙运行的底层规律。他们安静、内敛，但在感兴趣的话题上可以滔滔不绝。INTP重视精确性和逻辑性，对不合逻辑的事物缺乏耐心。',
    strengths: ['分析能力强', '客观公正', '想象力丰富', '思维开放', '诚实直率'],
    weaknesses: ['社交困难', '不切实际', '拖延', '情感表达弱', '过度思考'],
    careers: ['软件工程师', '数学家', '哲学家', '数据科学家', '游戏设计师'],
    famous: ['爱因斯坦', '比尔·盖茨', '达尔文', '亚伯拉罕·林肯'],
    loveStyle: '需要大量个人空间，寻找能理解自己思维方式的伴侣，用独特的方式表达爱意。',
    percentage: '3.3%',
  },
  ENTJ: {
    group: '分析家', groupColor: '#9B59B6',
    summary: '大胆、富有想象力且意志强大的领导者，总能找到或创造解决方法。',
    description: 'ENTJ是天生的领导者。他们果断、自信，善于组织和指挥他人。ENTJ拥有强大的战略眼光，能够看到大局并制定有效的计划。他们追求效率和成就，不惧怕挑战和冲突。',
    strengths: ['领导力强', '自信果断', '战略眼光', '高效执行', '意志坚定'],
    weaknesses: ['专横', '不耐烦', '傲慢', '情感冷漠', '过于苛刻'],
    careers: ['CEO', '律师', '企业顾问', '政治家', '军事指挥官'],
    famous: ['史蒂夫·乔布斯', '拿破仑', '玛格丽特·撒切尔', '朱利叶斯·凯撒'],
    loveStyle: '在感情中主动而热烈，寻找能与自己并肩作战的伴侣，重视忠诚和成长。',
    percentage: '1.8%',
  },
  ENTP: {
    group: '分析家', groupColor: '#9B59B6',
    summary: '聪明好奇的思想者，不会放弃任何智力上的挑战。',
    description: 'ENTP是最具创新精神的类型。他们思维敏捷，善于辩论，喜欢挑战传统观念。ENTP充满好奇心，总是在探索新的可能性。他们机智幽默，善于社交，但有时会因为追求新鲜感而缺乏持久性。',
    strengths: ['创新思维', '口才出众', '适应力强', '知识面广', '精力充沛'],
    weaknesses: ['好争辩', '缺乏耐心', '不够专注', '不切实际', '不善于执行'],
    careers: ['创业者', '产品经理', '记者', '营销策划', '发明家'],
    famous: ['本杰明·富兰克林', '马克·吐温', '托马斯·爱迪生', '钢铁侠'],
    loveStyle: '充满激情和创意，喜欢智力上的交锋，需要一个能跟上自己节奏的伴侣。',
    percentage: '3.2%',
  },
  INFJ: {
    group: '外交家', groupColor: '#2ECC71',
    summary: '安静而神秘，同时鼓舞人心且不知疲倦的理想主义者。',
    description: 'INFJ是最稀有的人格类型。他们拥有强烈的直觉和深刻的洞察力，能够理解他人的情感和动机。INFJ是理想主义者，致力于让世界变得更好。他们安静但坚定，有着强烈的价值观和使命感。',
    strengths: ['洞察力强', '有远见', '坚定执着', '富有同情心', '创造力强'],
    weaknesses: ['过于理想化', '容易倦怠', '过于敏感', '完美主义', '回避冲突'],
    careers: ['心理咨询师', '作家', '教师', '社会工作者', 'UX设计师'],
    famous: ['马丁·路德·金', '特蕾莎修女', '甘地', '尼尔森·曼德拉'],
    loveStyle: '深情而专一，寻找灵魂伴侣，重视精神层面的连接和共同的价值观。',
    percentage: '1.5%',
  },
  INFP: {
    group: '外交家', groupColor: '#2ECC71',
    summary: '诗意、善良的利他主义者，总是热情地为正义事业提供帮助。',
    description: 'INFP是真正的理想主义者，他们总是在寻找生活中的美好和意义。他们拥有丰富的内心世界，对艺术和创造有着天然的热爱。INFP善良、温柔，对他人的痛苦有着深切的同理心。',
    strengths: ['富有同理心', '创造力强', '开放包容', '热情投入', '忠诚可靠'],
    weaknesses: ['过于理想化', '自我封闭', '过度敏感', '不切实际', '难以决断'],
    careers: ['作家', '心理学家', '艺术家', '音乐家', '社会活动家'],
    famous: ['莎士比亚', '托尔金', '威廉·布莱克', '约翰·列侬'],
    loveStyle: '浪漫而深情，追求真挚的情感连接，愿意为爱付出一切。',
    percentage: '4.4%',
  },
  ENFJ: {
    group: '外交家', groupColor: '#2ECC71',
    summary: '富有魅力和鼓舞人心的领导者，能够吸引听众。',
    description: 'ENFJ是天生的导师和领袖。他们热情、有魅力，善于激励和引导他人。ENFJ对人际关系有着敏锐的洞察力，总是能够理解他人的需求并提供帮助。他们有强烈的责任感和使命感。',
    strengths: ['领导魅力', '善于沟通', '富有同理心', '组织能力强', '可靠负责'],
    weaknesses: ['过度付出', '过于理想化', '难以拒绝', '过度敏感', '自我牺牲'],
    careers: ['教师', '人力资源', '公关经理', '非营利组织领导', '培训师'],
    famous: ['奥巴马', '奥普拉', '马丁·路德·金', '本·阿弗莱克'],
    loveStyle: '热情而体贴，全心全意投入感情，善于营造温馨和谐的关系氛围。',
    percentage: '2.5%',
  },
  ENFP: {
    group: '外交家', groupColor: '#2ECC71',
    summary: '热情、有创造力、社交能力强的自由精灵，总能找到理由微笑。',
    description: 'ENFP是最具感染力的类型。他们充满热情和创造力，对生活充满好奇心。ENFP善于发现他人的潜力，是出色的激励者。他们追求自由和真实，不喜欢被规则束缚。',
    strengths: ['热情洋溢', '创造力强', '善于社交', '适应力强', '乐观积极'],
    weaknesses: ['缺乏专注', '过于理想化', '情绪化', '难以坚持', '过度承诺'],
    careers: ['记者', '演员', '创意总监', '企业家', '旅行博主'],
    famous: ['罗宾·威廉姆斯', '威尔·史密斯', 'RM(金南俊)', '马克·吐温'],
    loveStyle: '浪漫而充满激情，喜欢惊喜和新鲜感，需要一个能理解自己多变性格的伴侣。',
    percentage: '8.1%',
  },
  ISTJ: {
    group: '守卫者', groupColor: '#3498DB',
    summary: '实际且注重事实的个人，其可靠性不容置疑。',
    description: 'ISTJ是最可靠的人格类型。他们重视传统、秩序和责任，是社会的中坚力量。ISTJ做事有条不紊，注重细节，总是能够按时完成任务。他们诚实、正直，对承诺极为认真。',
    strengths: ['可靠负责', '注重细节', '诚实正直', '有条理', '意志坚定'],
    weaknesses: ['固执己见', '不善变通', '过于严肃', '不善表达情感', '抗拒变化'],
    careers: ['会计师', '审计员', '军人', '法官', '系统管理员'],
    famous: ['乔治·华盛顿', '安格拉·默克尔', '沃伦·巴菲特', '杰夫·贝佐斯'],
    loveStyle: '忠诚而稳定，用行动而非言语表达爱意，是最可靠的伴侣。',
    percentage: '11.6%',
  },
  ISFJ: {
    group: '守卫者', groupColor: '#3498DB',
    summary: '非常专注且温暖的保护者，时刻准备着保护所爱的人。',
    description: 'ISFJ是最体贴的人格类型。他们温暖、善良，总是默默地关心和照顾身边的人。ISFJ有着出色的记忆力，能记住他人的喜好和需求。他们谦虚低调，但在需要时会坚定地站出来保护所爱的人。',
    strengths: ['体贴入微', '可靠忠诚', '耐心细致', '观察力强', '勤劳踏实'],
    weaknesses: ['过度付出', '不善拒绝', '抗拒变化', '过于谦虚', '压抑情感'],
    careers: ['护士', '教师', '社工', '行政助理', '图书管理员'],
    famous: ['碧昂丝', '凯特王妃', '安妮·海瑟薇', '文在寅'],
    loveStyle: '温柔而体贴，用无微不至的关怀表达爱意，是最温暖的伴侣。',
    percentage: '13.8%',
  },
  ESTJ: {
    group: '守卫者', groupColor: '#3498DB',
    summary: '出色的管理者，在管理事物或人方面无与伦比。',
    description: 'ESTJ是天生的组织者和管理者。他们重视秩序、规则和效率，善于建立和维护系统。ESTJ直接、果断，有着强烈的责任感。他们是社区的支柱，总是愿意承担领导角色。',
    strengths: ['组织能力强', '果断高效', '忠诚可靠', '意志坚定', '实际务实'],
    weaknesses: ['固执', '不够灵活', '过于直接', '不善处理情感', '控制欲强'],
    careers: ['项目经理', '银行经理', '法官', '军官', '学校校长'],
    famous: ['亨利·福特', '希拉里·克林顿', '朱迪·丹奇', '索尼娅·索托马约尔'],
    loveStyle: '忠诚而负责，重视家庭和传统，用实际行动表达对伴侣的关爱。',
    percentage: '8.7%',
  },
  ESFJ: {
    group: '守卫者', groupColor: '#3498DB',
    summary: '非常关心他人的人，善于社交，受欢迎，总是热心帮助。',
    description: 'ESFJ是最善于社交的类型之一。他们热情、友善，总是关心他人的感受和需求。ESFJ重视和谐的人际关系，善于营造温馨的氛围。他们是出色的主人和组织者，总是确保每个人都感到舒适。',
    strengths: ['善于社交', '体贴关怀', '忠诚可靠', '组织能力强', '合作精神'],
    weaknesses: ['过于在意他人看法', '不善处理批评', '过度付出', '抗拒变化', '控制欲'],
    careers: ['人力资源', '护士', '教师', '活动策划', '客户服务经理'],
    famous: ['泰勒·斯威夫特', '詹妮弗·洛佩兹', '休·杰克曼', '比尔·克林顿'],
    loveStyle: '热情而体贴，善于营造浪漫氛围，重视家庭和伴侣的幸福。',
    percentage: '12.3%',
  },
  ISTP: {
    group: '探险家', groupColor: '#E67E22',
    summary: '大胆而实际的实验家，擅长使用各种形式的工具。',
    description: 'ISTP是最擅长动手的类型。他们好奇心强，喜欢探索事物的运作原理。ISTP冷静、理性，在危机时刻能保持镇定。他们追求自由和独立，不喜欢被规则束缚。',
    strengths: ['动手能力强', '冷静理性', '适应力强', '解决问题能力', '独立自主'],
    weaknesses: ['不善表达', '冒险冲动', '承诺困难', '不够敏感', '容易厌倦'],
    careers: ['工程师', '飞行员', '消防员', '机械师', '法医'],
    famous: ['克林特·伊斯特伍德', '布鲁斯·李', '迈克尔·乔丹', '汤姆·克鲁斯'],
    loveStyle: '独立而自由，用行动而非言语表达爱意，需要大量个人空间。',
    percentage: '5.4%',
  },
  ISFP: {
    group: '探险家', groupColor: '#E67E22',
    summary: '灵活而有魅力的艺术家，时刻准备着探索和体验新事物。',
    description: 'ISFP是最具艺术气质的类型。他们敏感、温柔，对美有着天然的感知力。ISFP活在当下，享受生活中的每一个美好瞬间。他们安静但内心丰富，通过艺术和行动表达自己。',
    strengths: ['艺术感强', '善良温柔', '灵活适应', '忠诚可靠', '观察力敏锐'],
    weaknesses: ['过于敏感', '回避冲突', '缺乏规划', '自我封闭', '难以坚持'],
    careers: ['艺术家', '设计师', '摄影师', '厨师', '兽医'],
    famous: ['迈克尔·杰克逊', '鲍勃·迪伦', '玛丽莲·梦露', '大卫·鲍伊'],
    loveStyle: '温柔而浪漫，用细腻的方式表达爱意，重视当下的美好时光。',
    percentage: '8.8%',
  },
  ESTP: {
    group: '探险家', groupColor: '#E67E22',
    summary: '聪明、精力充沛且善于感知的人，真正享受生活在边缘。',
    description: 'ESTP是最具行动力的类型。他们精力充沛，喜欢冒险和刺激。ESTP善于观察环境，能够快速做出反应。他们务实、直接，喜欢解决实际问题而非空谈理论。',
    strengths: ['行动力强', '善于社交', '观察力敏锐', '适应力强', '务实高效'],
    weaknesses: ['冲动冒险', '缺乏耐心', '不善规划', '不够敏感', '追求刺激'],
    careers: ['销售经理', '企业家', '运动员', '急救医生', '警察'],
    famous: ['唐纳德·特朗普', '麦当娜', '杰克·尼科尔森', '欧内斯特·海明威'],
    loveStyle: '充满激情和活力，喜欢冒险和新鲜感，用实际行动表达爱意。',
    percentage: '4.3%',
  },
  ESFP: {
    group: '探险家', groupColor: '#E67E22',
    summary: '自发的、精力充沛的表演者，生活在他们周围永远不会无聊。',
    description: 'ESFP是最具娱乐精神的类型。他们热情、开朗，总是能给周围的人带来欢乐。ESFP活在当下，善于享受生活中的每一刻。他们慷慨大方，喜欢与人分享快乐。',
    strengths: ['热情开朗', '善于社交', '乐观积极', '慷慨大方', '适应力强'],
    weaknesses: ['缺乏规划', '容易分心', '回避冲突', '过于感性', '不善长期坚持'],
    careers: ['演员', '活动策划', '旅游顾问', '健身教练', '公关专员'],
    famous: ['玛丽莲·梦露', '埃尔顿·约翰', '杰米·福克斯', '阿黛尔'],
    loveStyle: '热情而浪漫，喜欢制造惊喜和快乐，是最有趣的伴侣。',
    percentage: '8.5%',
  },
};

// 人格分组
const TYPE_GROUPS = [
  { name: '分析家', color: '#9B59B6', types: ['INTJ', 'INTP', 'ENTJ', 'ENTP'], icon: '🧠', desc: '理性与战略' },
  { name: '外交家', color: '#2ECC71', types: ['INFJ', 'INFP', 'ENFJ', 'ENFP'], icon: '💚', desc: '共情与理想' },
  { name: '守卫者', color: '#3498DB', types: ['ISTJ', 'ISFJ', 'ESTJ', 'ESFJ'], icon: '🛡️', desc: '秩序与责任' },
  { name: '探险家', color: '#E67E22', types: ['ISTP', 'ISFP', 'ESTP', 'ESFP'], icon: '🧭', desc: '灵活与自由' },
];

type ViewState = 'home' | 'test' | 'result' | 'history' | 'compatibility' | 'gallery' | 'typeDetail';

interface MBTITestProps {
  onClose: () => void;
  personId?: number;
}

const MBTITest: React.FC<MBTITestProps> = ({ onClose, personId }) => {
  const [viewState, setViewState] = useState<ViewState>('home');
  const [questions, setQuestions] = useState<MBTIQuestion[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<MBTIResult | null>(null);
  const [historyResults, setHistoryResults] = useState<MBTIResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selfResult, setSelfResult] = useState<MBTIResult | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [selectedType1, setSelectedType1] = useState('');
  const [selectedType2, setSelectedType2] = useState('');
  const [compatibility, setCompatibility] = useState<MBTICompatibility | null>(null);
  const [targetPersonId, setTargetPersonId] = useState<number | null>(personId || null);
  const [selectedTypeCode, setSelectedTypeCode] = useState<string>('');

  const backFromCurrentView = () => {
    if (viewState === 'home') return false;
    if (viewState === 'typeDetail') setViewState('gallery');
    else setViewState('home');
    return true;
  };

  useAppBackHandler(backFromCurrentView);

  useEffect(() => {
    loadSelfResult();
    loadHistory();
    loadPeople();
  }, []);

  const loadSelfResult = async () => {
    try {
      const response = await mbtiApi.getSelfLatestResult();
      if (response.data) {
        setSelfResult(response.data);
      }
    } catch (err) {
      console.error('加载自身MBTI结果失败:', err);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await mbtiApi.getAllResults();
      setHistoryResults(response.data || []);
    } catch (err) {
      console.error('加载历史结果失败:', err);
    }
  };

  const loadPeople = async () => {
    try {
      const response = await peopleApi.getAll();
      setPeople(response.data || []);
    } catch (err) {
      console.error('加载人员列表失败:', err);
    }
  };

  const startTest = async () => {
    setLoading(true);
    try {
      const response = await mbtiApi.getQuestions();
      setQuestions(response.data);
      setAnswers({});
      setCurrentQuestion(0);
      setViewState('test');
    } catch (err) {
      alert('加载题目失败，请检查网络连接');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (answer: string) => {
    const qId = String(questions[currentQuestion].id);
    const newAnswers = { ...answers, [qId]: answer };
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < 60) {
      alert(`请至少回答60道题目，当前已回答${Object.keys(answers).length}道`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await mbtiApi.submitTest({
        person_id: targetPersonId,
        answers,
      });
      setResult(response.data);
      setViewState('result');
      loadHistory();
      loadSelfResult();
    } catch (err: any) {
      alert('提交失败: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckCompatibility = async () => {
    if (!selectedType1 || !selectedType2) {
      alert('请选择两个MBTI类型');
      return;
    }
    try {
      const response = await mbtiApi.getCompatibility(selectedType1, selectedType2);
      setCompatibility(response.data);
    } catch (err: any) {
      alert('查询失败: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteResult = async (resultId: number) => {
    if (!window.confirm('确定要删除这个测试结果吗？')) return;
    try {
      await mbtiApi.deleteResult(resultId);
      loadHistory();
      loadSelfResult();
    } catch (err) {
      alert('删除失败');
    }
  };

  const getDimensionLabel = (dim: string) => {
    switch (dim) {
      case 'EI': return 'E 外向 — I 内向';
      case 'SN': return 'S 感觉 — N 直觉';
      case 'TF': return 'T 思考 — F 情感';
      case 'JP': return 'J 判断 — P 知觉';
      default: return dim;
    }
  };

  const progress = questions.length > 0 ? Math.round((Object.keys(answers).length / questions.length) * 100) : 0;

  // ==================== 渲染各视图 ====================

  const renderHome = () => (
    <div className="mbti-home">
      {/* 顶部卡片 */}
      <div className="mbti-hero-card">
        <div className="mbti-hero-emoji">🧠</div>
        <h2>MBTI 人格测试</h2>
        <p>探索你的性格类型，了解自己和他人</p>
        <button className="mbti-start-btn" onClick={startTest} disabled={loading}>
          {loading ? '加载中...' : '开始测试'}
        </button>
      </div>

      {/* 自己的结果 */}
      {selfResult && (
        <div className="mbti-self-card" onClick={() => { setResult(selfResult); setViewState('result'); }}>
          <div className="mbti-self-header">
            <span className="mbti-self-label">我的MBTI类型</span>
            <span className="mbti-self-date">{new Date(selfResult.created_at).toLocaleDateString('zh-CN')}</span>
          </div>
          <div className="mbti-self-type">
            <span className="mbti-type-emoji">{TYPE_INFO[selfResult.mbti_type]?.emoji}</span>
            <span className="mbti-type-code">{selfResult.mbti_type}</span>
            <span className="mbti-type-name">{TYPE_INFO[selfResult.mbti_type]?.name}</span>
          </div>
          <div className="mbti-mini-bars">
            {renderMiniBar('E', 'I', selfResult.ei_score)}
            {renderMiniBar('S', 'N', selfResult.sn_score)}
            {renderMiniBar('T', 'F', selfResult.tf_score)}
            {renderMiniBar('J', 'P', selfResult.jp_score)}
          </div>
        </div>
      )}

      {/* 为他人测试 */}
      <div className="mbti-section">
        <h3>🎯 为他人测试</h3>
        <p className="mbti-section-desc">根据你对他人的了解，代替他们完成测试</p>
        <select
          className="mbti-select"
          value={targetPersonId || ''}
          onChange={(e) => setTargetPersonId(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">为自己测试</option>
          {people.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <button className="mbti-action-btn" onClick={startTest} disabled={loading}>
          {targetPersonId ? `为 ${people.find(p => p.id === targetPersonId)?.name} 测试` : '为自己测试'}
        </button>
      </div>

      {/* 兼容性分析 */}
      <div className="mbti-section">
        <h3>💕 兼容性分析</h3>
        <p className="mbti-section-desc">查看两个MBTI类型之间的兼容性</p>
        <div className="mbti-compat-selects">
          <select className="mbti-select" value={selectedType1} onChange={e => setSelectedType1(e.target.value)}>
            <option value="">选择类型1</option>
            {Object.keys(TYPE_INFO).map(t => (
              <option key={t} value={t}>{TYPE_INFO[t].emoji} {t} - {TYPE_INFO[t].name}</option>
            ))}
          </select>
          <span className="mbti-vs">VS</span>
          <select className="mbti-select" value={selectedType2} onChange={e => setSelectedType2(e.target.value)}>
            <option value="">选择类型2</option>
            {Object.keys(TYPE_INFO).map(t => (
              <option key={t} value={t}>{TYPE_INFO[t].emoji} {t} - {TYPE_INFO[t].name}</option>
            ))}
          </select>
        </div>
        <button className="mbti-action-btn" onClick={handleCheckCompatibility}>
          查看兼容性
        </button>
        {compatibility && renderCompatibilityResult()}
      </div>

      {/* 16种人格图鉴入口 */}
      <div className="mbti-section" style={{ cursor: 'pointer' }} onClick={() => setViewState('gallery')}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3>🃏 16种人格图鉴</h3>
            <p className="mbti-section-desc" style={{ margin: 0 }}>浏览所有MBTI人格类型的详细介绍</p>
          </div>
          <span style={{ fontSize: 24, color: 'var(--text-tertiary)' }}>›</span>
        </div>
      </div>

      {/* 历史记录 */}
      {historyResults.length > 0 && (
        <div className="mbti-section">
          <h3>📊 测试历史</h3>
          <div className="mbti-history-list">
            {historyResults.slice(0, 10).map(r => (
              <div key={r.id} className="mbti-history-item" onClick={() => { setResult(r); setViewState('result'); }}>
                <div className="mbti-history-left">
                  <span className="mbti-history-emoji">{TYPE_INFO[r.mbti_type]?.emoji}</span>
                  <div>
                    <div className="mbti-history-type">{r.mbti_type} - {TYPE_INFO[r.mbti_type]?.name}</div>
                    <div className="mbti-history-person">{r.person_name || '我自己'}</div>
                  </div>
                </div>
                <div className="mbti-history-right">
                  <span className="mbti-history-date">{new Date(r.created_at).toLocaleDateString('zh-CN')}</span>
                  <button
                    className="mbti-delete-btn"
                    onClick={(e) => { e.stopPropagation(); handleDeleteResult(r.id); }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderMiniBar = (leftLabel: string, rightLabel: string, score: number) => (
    <div className="mbti-mini-bar-row">
      <span className={`mbti-mini-label ${score <= 50 ? 'active' : ''}`}>{leftLabel}</span>
      <div className="mbti-mini-bar-track">
        <div
          className="mbti-mini-bar-fill"
          style={{ width: `${100 - score}%`, background: score <= 50 ? 'var(--primary)' : '#ddd' }}
        />
      </div>
      <div className="mbti-mini-bar-track">
        <div
          className="mbti-mini-bar-fill right"
          style={{ width: `${score}%`, background: score > 50 ? 'var(--primary)' : '#ddd' }}
        />
      </div>
      <span className={`mbti-mini-label ${score > 50 ? 'active' : ''}`}>{rightLabel}</span>
    </div>
  );

  const renderCompatibilityResult = () => {
    if (!compatibility) return null;
    const score = compatibility.compatibility_score;
    let scoreColor = '#27AE60';
    if (score < 50) scoreColor = '#E74C3C';
    else if (score < 70) scoreColor = '#F39C12';
    else if (score < 85) scoreColor = '#2980B9';

    return (
      <div className="mbti-compat-result">
        <div className="mbti-compat-types">
          <span>{TYPE_INFO[compatibility.type1]?.emoji} {compatibility.type1}</span>
          <span className="mbti-compat-heart">💕</span>
          <span>{TYPE_INFO[compatibility.type2]?.emoji} {compatibility.type2}</span>
        </div>
        <div className="mbti-compat-score" style={{ color: scoreColor }}>
          {score}分
        </div>
        <div className="mbti-compat-bar">
          <div className="mbti-compat-bar-fill" style={{ width: `${score}%`, background: scoreColor }} />
        </div>
        <div className="mbti-compat-type-label">{compatibility.relationship_type}</div>
        <p className="mbti-compat-desc">{compatibility.description}</p>
      </div>
    );
  };

  const renderTest = () => {
    if (questions.length === 0) return <div className="loading">加载中...</div>;

    const q = questions[currentQuestion];
    const currentAnswer = answers[String(q.id)];
    const answeredCount = Object.keys(answers).length;

    return (
      <div className="mbti-test">
        {/* 进度条 */}
        <div className="mbti-progress-section">
          <div className="mbti-progress-info">
            <span>第 {currentQuestion + 1} / {questions.length} 题</span>
            <span>{getDimensionLabel(q.dimension)}</span>
          </div>
          <div className="mbti-progress-bar">
            <div className="mbti-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="mbti-progress-percent">{progress}% 完成</div>
        </div>

        {/* 题目 */}
        <div className="mbti-question-card">
          <div className="mbti-question-number">Q{currentQuestion + 1}</div>
          <h3 className="mbti-question-text">{q.question}</h3>

          <div className="mbti-options">
            <button
              className={`mbti-option ${currentAnswer === 'A' ? 'selected' : ''}`}
              onClick={() => handleAnswer('A')}
            >
              <span className="mbti-option-letter">A</span>
              <span className="mbti-option-text">{q.option_a}</span>
            </button>
            <button
              className={`mbti-option ${currentAnswer === 'B' ? 'selected' : ''}`}
              onClick={() => handleAnswer('B')}
            >
              <span className="mbti-option-letter">B</span>
              <span className="mbti-option-text">{q.option_b}</span>
            </button>
          </div>
        </div>

        {/* 导航按钮 */}
        <div className="mbti-nav-buttons">
          <button
            className="mbti-nav-btn prev"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
          >
            ← 上一题
          </button>

          {currentQuestion < questions.length - 1 ? (
            <button
              className="mbti-nav-btn next"
              onClick={() => setCurrentQuestion(currentQuestion + 1)}
              disabled={!currentAnswer}
            >
              下一题 →
            </button>
          ) : (
            <button
              className="mbti-nav-btn submit"
              onClick={handleSubmit}
              disabled={submitting || answeredCount < 60}
            >
              {submitting ? '提交中...' : `提交 (${answeredCount}/${questions.length})`}
            </button>
          )}
        </div>

        {/* 快速跳转 */}
        <div className="mbti-question-dots">
          {questions.map((_, i) => (
            <button
              key={i}
              className={`mbti-dot ${i === currentQuestion ? 'current' : ''} ${answers[String(questions[i].id)] ? 'answered' : ''}`}
              onClick={() => setCurrentQuestion(i)}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderResult = () => {
    if (!result) return null;

    const typeInfo = TYPE_INFO[result.mbti_type];

    return (
      <div className="mbti-result">
        {/* 类型展示卡片 */}
        <div className="mbti-result-hero" style={{ background: `linear-gradient(135deg, ${typeInfo?.color || '#6C5CE7'}, ${typeInfo?.color || '#6C5CE7'}88)` }}>
          <div className="mbti-result-emoji">{typeInfo?.emoji}</div>
          <div className="mbti-result-type">{result.mbti_type}</div>
          <div className="mbti-result-name">{typeInfo?.name}</div>
          {result.person_name && (
            <div className="mbti-result-person">测试对象: {result.person_name}</div>
          )}
        </div>

        {/* 维度详情 */}
        <div className="mbti-dimensions-card">
          <h3>维度分析</h3>
          {renderDimensionBar('外向 E', '内向 I', result.ei_score, '#E74C3C', '#3498DB')}
          {renderDimensionBar('感觉 S', '直觉 N', result.sn_score, '#27AE60', '#9B59B6')}
          {renderDimensionBar('思考 T', '情感 F', result.tf_score, '#2C3E50', '#E91E63')}
          {renderDimensionBar('判断 J', '知觉 P', result.jp_score, '#F39C12', '#1ABC9C')}
        </div>

        {/* 描述 */}
        {result.description && (
          <div className="mbti-info-card">
            <h3>📖 性格描述</h3>
            <p>{result.description}</p>
          </div>
        )}

        {/* 优势 */}
        {result.strengths && (
          <div className="mbti-info-card strengths">
            <h3>💪 优势</h3>
            <div className="mbti-tags">
              {result.strengths.split('、').map((s, i) => (
                <span key={i} className="mbti-tag strength">{s}</span>
              ))}
            </div>
          </div>
        )}

        {/* 劣势 */}
        {result.weaknesses && (
          <div className="mbti-info-card weaknesses">
            <h3>⚠️ 需要注意</h3>
            <div className="mbti-tags">
              {result.weaknesses.split('、').map((s, i) => (
                <span key={i} className="mbti-tag weakness">{s}</span>
              ))}
            </div>
          </div>
        )}

        {/* 操作按钮 */}
        <div className="mbti-result-actions">
          <button className="mbti-action-btn" onClick={() => { setViewState('home'); setResult(null); }}>
            返回首页
          </button>
          <button className="mbti-action-btn secondary" onClick={() => { setAnswers({}); setCurrentQuestion(0); startTest(); }}>
            重新测试
          </button>
        </div>
      </div>
    );
  };

  const renderDimensionBar = (leftLabel: string, rightLabel: string, score: number, leftColor: string, rightColor: string) => {
    const leftPercent = 100 - score;
    const rightPercent = score;
    const isRight = score > 50;

    return (
      <div className="mbti-dim-row">
        <div className="mbti-dim-labels">
          <span className={`mbti-dim-label ${!isRight ? 'active' : ''}`} style={!isRight ? { color: leftColor } : {}}>
            {leftLabel} {!isRight ? `${Math.round(leftPercent)}%` : ''}
          </span>
          <span className={`mbti-dim-label ${isRight ? 'active' : ''}`} style={isRight ? { color: rightColor } : {}}>
            {isRight ? `${Math.round(rightPercent)}%` : ''} {rightLabel}
          </span>
        </div>
        <div className="mbti-dim-bar">
          <div className="mbti-dim-bar-left" style={{ width: `${leftPercent}%`, background: leftColor, opacity: isRight ? 0.3 : 1 }} />
          <div className="mbti-dim-bar-right" style={{ width: `${rightPercent}%`, background: rightColor, opacity: !isRight ? 0.3 : 1 }} />
        </div>
      </div>
    );
  };

  const renderGallery = () => (
    <div className="mbti-gallery">
      {TYPE_GROUPS.map(group => (
        <div key={group.name} className="mbti-gallery-group">
          <div className="mbti-gallery-group-header">
            <span className="mbti-gallery-group-icon">{group.icon}</span>
            <div>
              <h3 style={{ margin: 0, color: group.color }}>{group.name}</h3>
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{group.desc}</span>
            </div>
          </div>
          <div className="mbti-gallery-grid">
            {group.types.map(code => {
              const info = TYPE_INFO[code];
              const detail = TYPE_DETAILS[code];
              return (
                <div key={code} className="mbti-gallery-card" onClick={() => { setSelectedTypeCode(code); setViewState('typeDetail'); }}
                  style={{ '--card-color': info.color } as React.CSSProperties}>
                  <div className="mbti-gallery-card-top" style={{ background: `linear-gradient(135deg, ${info.color}, ${info.color}99)` }}>
                    <span className="mbti-gallery-card-emoji">{info.emoji}</span>
                    <span className="mbti-gallery-card-code">{code}</span>
                  </div>
                  <div className="mbti-gallery-card-body">
                    <div className="mbti-gallery-card-name">{info.name}</div>
                    <div className="mbti-gallery-card-summary">{detail?.summary?.slice(0, 30)}...</div>
                    <div className="mbti-gallery-card-pct">{detail?.percentage} 人口</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  const renderTypeDetail = () => {
    const code = selectedTypeCode;
    const info = TYPE_INFO[code];
    const detail = TYPE_DETAILS[code];
    if (!info || !detail) return <div>未找到该类型</div>;

    return (
      <div className="mbti-type-detail">
        {/* Hero */}
        <div className="mbti-td-hero" style={{ background: `linear-gradient(135deg, ${info.color}, ${info.color}88)` }}>
          <div style={{ fontSize: 64 }}>{info.emoji}</div>
          <div style={{ fontSize: 42, fontWeight: 900, letterSpacing: 4 }}>{code}</div>
          <div style={{ fontSize: 20, fontWeight: 600, opacity: 0.9 }}>{info.name}</div>
          <div className="mbti-td-group-badge" style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 16px', borderRadius: 20, marginTop: 8, fontSize: 13 }}>
            {detail.group} · {detail.percentage} 人口
          </div>
        </div>

        {/* Summary */}
        <div className="mbti-info-card">
          <h3>✨ 一句话概括</h3>
          <p style={{ fontSize: 16, fontWeight: 500, color: info.color }}>{detail.summary}</p>
        </div>

        {/* Description */}
        <div className="mbti-info-card">
          <h3>📖 详细描述</h3>
          <p>{detail.description}</p>
        </div>

        {/* Strengths */}
        <div className="mbti-info-card">
          <h3>💪 优势</h3>
          <div className="mbti-tags">{detail.strengths.map((s, i) => <span key={i} className="mbti-tag strength">{s}</span>)}</div>
        </div>

        {/* Weaknesses */}
        <div className="mbti-info-card">
          <h3>⚠️ 需要注意</h3>
          <div className="mbti-tags">{detail.weaknesses.map((s, i) => <span key={i} className="mbti-tag weakness">{s}</span>)}</div>
        </div>

        {/* Careers */}
        <div className="mbti-info-card">
          <h3>💼 适合职业</h3>
          <div className="mbti-tags">{detail.careers.map((s, i) => <span key={i} className="mbti-tag" style={{ background: 'rgba(108,92,231,0.1)', color: '#6C5CE7' }}>{s}</span>)}</div>
        </div>

        {/* Love */}
        <div className="mbti-info-card">
          <h3>💕 恋爱风格</h3>
          <p>{detail.loveStyle}</p>
        </div>

        {/* Famous */}
        <div className="mbti-info-card">
          <h3>🌟 代表人物</h3>
          <div className="mbti-tags">{detail.famous.map((s, i) => <span key={i} className="mbti-tag" style={{ background: 'rgba(241,196,15,0.1)', color: '#F39C12' }}>{s}</span>)}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="mbti-container">
      {/* 顶部导航 */}
      <div className="mbti-header">
        <button className="mbti-back-btn" onClick={
          viewState === 'home' ? onClose : () => backFromCurrentView()
        }>
          ← {viewState === 'home' ? '返回' : viewState === 'typeDetail' ? '图鉴' : '首页'}
        </button>
        <h2 className="mbti-title">
          {viewState === 'test' ? 'MBTI 测试' :
           viewState === 'result' ? '测试结果' :
           viewState === 'gallery' ? '16种人格图鉴' :
           viewState === 'typeDetail' ? `${selectedTypeCode} ${TYPE_INFO[selectedTypeCode]?.name || ''}` :
           'MBTI 人格测试'}
        </h2>
        <div style={{ width: 60 }} />
      </div>

      {/* 内容区域 */}
      <div className="mbti-content">
        {viewState === 'home' && renderHome()}
        {viewState === 'test' && renderTest()}
        {viewState === 'result' && renderResult()}
        {viewState === 'gallery' && renderGallery()}
        {viewState === 'typeDetail' && renderTypeDetail()}
      </div>

      <style>{`
        .mbti-container {
          min-height: 100vh;
          background: var(--bg);
        }

        .mbti-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          padding-top: calc(16px + var(--status-bar-height, 28px));
          background: linear-gradient(135deg, #6C5CE7, #A29BFE);
          color: white;
        }

        .mbti-back-btn {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
          font-family: inherit;
        }

        .mbti-title {
          font-size: 18px;
          font-weight: 700;
          margin: 0;
        }

        .mbti-content {
          padding: 20px;
          max-width: 680px;
          margin: 0 auto;
        }

        /* ===== Home ===== */
        .mbti-hero-card {
          background: linear-gradient(135deg, #6C5CE7, #A29BFE);
          border-radius: 20px;
          padding: 32px 24px;
          text-align: center;
          color: white;
          margin-bottom: 20px;
          box-shadow: 0 8px 32px rgba(108, 92, 231, 0.3);
        }

        .mbti-hero-emoji {
          font-size: 56px;
          margin-bottom: 12px;
        }

        .mbti-hero-card h2 {
          margin: 0 0 8px 0;
          font-size: 24px;
        }

        .mbti-hero-card p {
          margin: 0 0 20px 0;
          opacity: 0.85;
          font-size: 14px;
        }

        .mbti-start-btn {
          background: white;
          color: #6C5CE7;
          border: none;
          padding: 14px 40px;
          border-radius: 25px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          font-family: inherit;
          transition: transform 0.2s;
        }

        .mbti-start-btn:active {
          transform: scale(0.95);
        }

        .mbti-start-btn:disabled {
          opacity: 0.6;
        }

        /* Self card */
        .mbti-self-card {
          background: var(--surface);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: var(--shadow-sm);
          cursor: pointer;
          transition: transform 0.2s;
          border: 1px solid var(--border);
        }

        .mbti-self-card:active {
          transform: scale(0.98);
        }

        .mbti-self-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .mbti-self-label {
          font-size: 13px;
          color: var(--text-tertiary);
          font-weight: 600;
        }

        .mbti-self-date {
          font-size: 12px;
          color: var(--text-tertiary);
        }

        .mbti-self-type {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }

        .mbti-type-emoji {
          font-size: 32px;
        }

        .mbti-type-code {
          font-size: 28px;
          font-weight: 800;
          color: var(--primary);
          letter-spacing: 2px;
        }

        .mbti-type-name {
          font-size: 16px;
          color: var(--text-secondary);
          font-weight: 500;
        }

        /* Mini bars */
        .mbti-mini-bars {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .mbti-mini-bar-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mbti-mini-label {
          font-size: 12px;
          font-weight: 700;
          width: 14px;
          text-align: center;
          color: var(--text-tertiary);
        }

        .mbti-mini-label.active {
          color: var(--primary);
        }

        .mbti-mini-bar-track {
          flex: 1;
          height: 6px;
          background: var(--surface-hover);
          border-radius: 3px;
          overflow: hidden;
        }

        .mbti-mini-bar-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.5s ease;
        }

        .mbti-mini-bar-fill.right {
          margin-left: auto;
        }

        /* Sections */
        .mbti-section {
          background: var(--surface);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--border);
        }

        .mbti-section h3 {
          margin: 0 0 8px 0;
          font-size: 17px;
        }

        .mbti-section-desc {
          color: var(--text-tertiary);
          font-size: 13px;
          margin: 0 0 16px 0;
        }

        .mbti-select {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid var(--border);
          border-radius: 12px;
          font-size: 14px;
          font-family: inherit;
          background: var(--bg);
          color: var(--text-primary);
          margin-bottom: 12px;
          appearance: none;
          -webkit-appearance: none;
        }

        .mbti-action-btn {
          width: 100%;
          padding: 12px;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
          transition: transform 0.2s;
        }

        .mbti-action-btn:active {
          transform: scale(0.97);
        }

        .mbti-action-btn.secondary {
          background: var(--surface-hover);
          color: var(--text-secondary);
        }

        .mbti-compat-selects {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
        }

        .mbti-compat-selects .mbti-select {
          margin-bottom: 0;
        }

        .mbti-vs {
          font-weight: 800;
          color: var(--primary);
          font-size: 14px;
          flex-shrink: 0;
        }

        /* Compatibility result */
        .mbti-compat-result {
          margin-top: 16px;
          padding: 20px;
          background: var(--bg);
          border-radius: 12px;
          text-align: center;
        }

        .mbti-compat-types {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .mbti-compat-heart {
          font-size: 24px;
        }

        .mbti-compat-score {
          font-size: 48px;
          font-weight: 800;
          margin-bottom: 8px;
        }

        .mbti-compat-bar {
          height: 8px;
          background: #eee;
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 12px;
        }

        .mbti-compat-bar-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.5s ease;
        }

        .mbti-compat-type-label {
          font-size: 16px;
          font-weight: 700;
          color: var(--primary);
          margin-bottom: 8px;
        }

        .mbti-compat-desc {
          color: var(--text-secondary);
          font-size: 14px;
          margin: 0;
        }

        /* History */
        .mbti-history-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .mbti-history-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: var(--bg);
          border-radius: 12px;
          cursor: pointer;
          transition: transform 0.2s;
        }

        .mbti-history-item:active {
          transform: scale(0.98);
        }

        .mbti-history-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .mbti-history-emoji {
          font-size: 24px;
        }

        .mbti-history-type {
          font-weight: 600;
          font-size: 14px;
        }

        .mbti-history-person {
          font-size: 12px;
          color: var(--text-tertiary);
        }

        .mbti-history-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mbti-history-date {
          font-size: 12px;
          color: var(--text-tertiary);
        }

        .mbti-delete-btn {
          background: none;
          border: none;
          color: var(--text-tertiary);
          font-size: 14px;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 8px;
        }

        .mbti-delete-btn:hover {
          background: rgba(231, 76, 60, 0.1);
          color: #E74C3C;
        }

        /* ===== Test ===== */
        .mbti-test {
          padding-bottom: 40px;
        }

        .mbti-progress-section {
          margin-bottom: 24px;
        }

        .mbti-progress-info {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }

        .mbti-progress-bar {
          height: 8px;
          background: var(--surface-hover);
          border-radius: 4px;
          overflow: hidden;
        }

        .mbti-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #6C5CE7, #A29BFE);
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .mbti-progress-percent {
          text-align: right;
          font-size: 12px;
          color: var(--text-tertiary);
          margin-top: 4px;
        }

        .mbti-question-card {
          background: var(--surface);
          border-radius: 20px;
          padding: 28px 24px;
          margin-bottom: 20px;
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--border);
        }

        .mbti-question-number {
          display: inline-block;
          background: linear-gradient(135deg, #6C5CE7, #A29BFE);
          color: white;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .mbti-question-text {
          font-size: 18px;
          font-weight: 600;
          line-height: 1.6;
          margin: 0 0 24px 0;
          color: var(--text-primary);
        }

        .mbti-options {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .mbti-option {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 16px 20px;
          background: var(--bg);
          border: 2px solid var(--border);
          border-radius: 14px;
          cursor: pointer;
          font-family: inherit;
          text-align: left;
          transition: all 0.2s;
          color: var(--text-primary);
        }

        .mbti-option:active {
          transform: scale(0.98);
        }

        .mbti-option.selected {
          border-color: var(--primary);
          background: rgba(108, 92, 231, 0.08);
        }

        .mbti-option-letter {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: var(--surface-hover);
          font-weight: 700;
          font-size: 14px;
          flex-shrink: 0;
          color: var(--text-secondary);
        }

        .mbti-option.selected .mbti-option-letter {
          background: var(--primary);
          color: white;
        }

        .mbti-option-text {
          font-size: 15px;
          line-height: 1.5;
          padding-top: 3px;
        }

        .mbti-nav-buttons {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
        }

        .mbti-nav-btn {
          flex: 1;
          padding: 14px;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
          transition: all 0.2s;
        }

        .mbti-nav-btn.prev {
          background: var(--surface-hover);
          color: var(--text-secondary);
        }

        .mbti-nav-btn.next, .mbti-nav-btn.submit {
          background: var(--primary);
          color: white;
        }

        .mbti-nav-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .mbti-nav-btn:active:not(:disabled) {
          transform: scale(0.97);
        }

        .mbti-question-dots {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          justify-content: center;
          padding: 16px;
          background: var(--surface);
          border-radius: 16px;
          border: 1px solid var(--border);
        }

        .mbti-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: none;
          background: var(--surface-hover);
          cursor: pointer;
          padding: 0;
          transition: all 0.2s;
        }

        .mbti-dot.answered {
          background: var(--primary);
          opacity: 0.5;
        }

        .mbti-dot.current {
          background: var(--primary);
          opacity: 1;
          transform: scale(1.3);
        }

        /* ===== Result ===== */
        .mbti-result {
          padding-bottom: 40px;
        }

        .mbti-result-hero {
          border-radius: 24px;
          padding: 40px 24px;
          text-align: center;
          color: white;
          margin-bottom: 20px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.15);
        }

        .mbti-result-emoji {
          font-size: 64px;
          margin-bottom: 12px;
        }

        .mbti-result-type {
          font-size: 48px;
          font-weight: 900;
          letter-spacing: 6px;
          margin-bottom: 4px;
        }

        .mbti-result-name {
          font-size: 20px;
          font-weight: 600;
          opacity: 0.9;
        }

        .mbti-result-person {
          margin-top: 12px;
          font-size: 14px;
          opacity: 0.8;
          background: rgba(255,255,255,0.2);
          display: inline-block;
          padding: 4px 16px;
          border-radius: 20px;
        }

        /* Dimensions */
        .mbti-dimensions-card {
          background: var(--surface);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 16px;
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--border);
        }

        .mbti-dimensions-card h3 {
          margin: 0 0 16px 0;
          font-size: 16px;
        }

        .mbti-dim-row {
          margin-bottom: 16px;
        }

        .mbti-dim-row:last-child {
          margin-bottom: 0;
        }

        .mbti-dim-labels {
          display: flex;
          justify-content: space-between;
          margin-bottom: 6px;
        }

        .mbti-dim-label {
          font-size: 13px;
          color: var(--text-tertiary);
          font-weight: 500;
        }

        .mbti-dim-label.active {
          font-weight: 700;
        }

        .mbti-dim-bar {
          display: flex;
          height: 12px;
          border-radius: 6px;
          overflow: hidden;
          background: var(--surface-hover);
        }

        .mbti-dim-bar-left, .mbti-dim-bar-right {
          height: 100%;
          transition: width 0.5s ease;
        }

        /* Info cards */
        .mbti-info-card {
          background: var(--surface);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 16px;
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--border);
        }

        .mbti-info-card h3 {
          margin: 0 0 12px 0;
          font-size: 16px;
        }

        .mbti-info-card p {
          margin: 0;
          line-height: 1.8;
          color: var(--text-secondary);
          font-size: 14px;
        }

        .mbti-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .mbti-tag {
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
        }

        .mbti-tag.strength {
          background: rgba(39, 174, 96, 0.1);
          color: #27AE60;
        }

        .mbti-tag.weakness {
          background: rgba(231, 76, 60, 0.1);
          color: #E74C3C;
        }

        .mbti-result-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .mbti-result-actions .mbti-action-btn {
          flex: 1;
        }

        /* ===== Gallery ===== */
        .mbti-gallery {
          padding-bottom: 40px;
        }

        .mbti-gallery-group {
          margin-bottom: 28px;
        }

        .mbti-gallery-group-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 14px;
          padding: 0 4px;
        }

        .mbti-gallery-group-icon {
          font-size: 28px;
        }

        .mbti-gallery-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .mbti-gallery-card {
          background: var(--surface);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--border-light);
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .mbti-gallery-card:active {
          transform: scale(0.96);
          box-shadow: var(--shadow-md);
        }

        .mbti-gallery-card-top {
          padding: 20px 16px 14px;
          text-align: center;
          color: white;
          position: relative;
        }

        .mbti-gallery-card-emoji {
          font-size: 36px;
          display: block;
          margin-bottom: 6px;
        }

        .mbti-gallery-card-code {
          font-size: 20px;
          font-weight: 900;
          letter-spacing: 3px;
        }

        .mbti-gallery-card-body {
          padding: 12px 14px 14px;
          text-align: center;
        }

        .mbti-gallery-card-name {
          font-size: 15px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .mbti-gallery-card-summary {
          font-size: 12px;
          color: var(--text-tertiary);
          line-height: 1.4;
          margin-bottom: 6px;
        }

        .mbti-gallery-card-pct {
          font-size: 11px;
          color: var(--text-tertiary);
          background: var(--bg);
          display: inline-block;
          padding: 2px 10px;
          border-radius: 10px;
        }

        /* ===== Type Detail ===== */
        .mbti-type-detail {
          padding-bottom: 40px;
        }

        .mbti-td-hero {
          border-radius: 24px;
          padding: 36px 24px;
          text-align: center;
          color: white;
          margin-bottom: 20px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.15);
        }

        @media (max-width: 480px) {
          .mbti-gallery-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
          .mbti-gallery-card-top {
            padding: 16px 12px 10px;
          }
          .mbti-gallery-card-emoji {
            font-size: 28px;
          }
          .mbti-gallery-card-code {
            font-size: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default MBTITest;
